
import React, { useState, useRef, useEffect } from 'react';
import { Category, ListingType, Lead } from '../types';
import { ArrowLeft, CheckCircle, ShieldCheck, Home, Building2, Trees, ChevronDown, X, Zap, Sparkles, MapPin, Banknote, FileText, Loader2, Search, Crown, Check, AlertCircle, TrendingUp } from 'lucide-react';
import { api } from '../lib/db';
import { useLanguage } from '../contexts/LanguageContext';
import * as Constants from '../constants';
import { useToast } from '../contexts/ToastContext';
import { locationService } from '../lib/locationService';
import { useCreateLead } from '../hooks/useCustomerData';
import { useNavigate, useParams } from 'react-router-dom';
import SEO from './SEO';
import PaymentModal from './PaymentModal';

const CURRENCIES = ['TL', 'USD', 'EUR'];

const Label = ({ children, required = false }: { children?: React.ReactNode; required?: boolean }) => (
  <label className="block text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2 ml-1">
    {children}
    {required && <span className="text-red-500 ml-1" title="Doldurulması Zorunludur">*</span>}
  </label>
);

// Zorunlu alan açıklaması için yardımcı bileşen
const RequiredFieldNote = () => (
  <div className="flex items-center text-xs text-gray-400 dark:text-gray-500 mb-6">
    <span className="text-red-500 mr-1">*</span>
    <span>Doldurulması zorunlu alanlar</span>
  </div>
);

const BaseStyles = "w-full px-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl text-gray-900 dark:text-white placeholder-gray-400 focus:bg-white dark:focus:bg-gray-950 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all duration-200 ease-in-out font-medium";
const InputBaseClass = `${BaseStyles} h-[54px]`;
const TextareaBaseClass = `${BaseStyles} h-48 py-4 resize-none`;

const SelectWrapper = ({ children, icon }: { children?: React.ReactNode, icon?: React.ReactNode }) => (
  <div className="relative">
    {children}
    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
      {icon || <ChevronDown className="w-5 h-5" />}
    </div>
  </div>
);

// Türkçe karakterleri normalize eden helper fonksiyon
// Mobil klavye ve tüm Unicode varyasyonlarını handle eder
const normalizeTurkish = (text: string): string => {
  if (!text || typeof text !== 'string') return '';

  // Türkçe karakter mapping - tüm varyasyonları kapsar
  const turkishCharMap: Record<string, string> = {
    // İ/i/ı varyasyonları (U+0130 İ, U+0131 ı)
    'İ': 'i', 'I': 'i', 'ı': 'i', 'i': 'i',
    // Ğ/ğ
    'Ğ': 'g', 'ğ': 'g', 'G': 'g', 'g': 'g',
    // Ü/ü
    'Ü': 'u', 'ü': 'u', 'U': 'u', 'u': 'u',
    // Ş/ş
    'Ş': 's', 'ş': 's', 'S': 's', 's': 's',
    // Ö/ö
    'Ö': 'o', 'ö': 'o', 'O': 'o', 'o': 'o',
    // Ç/ç
    'Ç': 'c', 'ç': 'c', 'C': 'c', 'c': 'c',
  };

  let normalized = text
    // NFD normalizasyonu (birleşik karakterleri ayır)
    .normalize('NFD')
    // Combining diacritics kaldır (nokta, çizgi, vs.)
    .replace(/[\u0300-\u036f]/g, '')
    // Türkçe locale ile küçük harfe çevir (daha doğru)
    .toLocaleLowerCase('tr-TR');

  // Manuel karakter değiştirme (tüm varyasyonları kapsar)
  normalized = normalized
    .split('')
    .map(char => turkishCharMap[char] || char)
    .join('');

  // Türkçe karakterleri tekrar normalize et (güvenlik için)
  return normalized
    .replace(/[İIı]/g, 'i')
    .replace(/[Ğğ]/g, 'g')
    .replace(/[Üü]/g, 'u')
    .replace(/[Şş]/g, 's')
    .replace(/[Öö]/g, 'o')
    .replace(/[Çç]/g, 'c')
    .replace(/\s+/g, ' ')
    .trim();
};

// XSS PROTECTION: Dangerous patterns to detect on frontend
const XSS_PATTERNS = [
  /<script/i,
  /javascript:/i,
  /on\w+\s*=/i,           // onerror=, onclick=, onload= etc.
  /<img[^>]+src\s*=/i,    // <img src=
  /<iframe/i,
  /<object/i,
  /<embed/i,
  /<svg[^>]+on/i,
  /data:/i,
  /vbscript:/i,
  /<link/i,
  /<style/i,
  /<meta/i,
];

// XSS PROTECTION: Check if text contains malicious patterns
const containsXSS = (text: string): boolean => {
  if (!text) return false;
  return XSS_PATTERNS.some(pattern => pattern.test(text));
};

// XSS PROTECTION: Validate input and return error message if malicious
const validateInputXSS = (text: string, fieldName: string): string | null => {
  if (!text) return null;
  if (containsXSS(text)) {
    console.warn(`[SECURITY] XSS attempt blocked in field "${fieldName}":`, text.substring(0, 100));
    return `${fieldName} alanında geçersiz karakterler tespit edildi. Lütfen HTML kodları kullanmayınız.`;
  }
  return null;
};

const ModernSingleSelect = ({ options, selected, onChange, label, placeholder, searchEnabled = false, isLoading = false, required = false }: any) => {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [wrapperRef]);

  // Arama terimini normalize et (her render'da)
  const normalizedSearchTerm = normalizeTurkish(searchTerm);

  const filteredOptions = searchEnabled
    ? options.filter((opt: string) => {
      const normalizedOpt = normalizeTurkish(opt);
      // Hem includes hem de startsWith kontrolü (daha esnek)
      return normalizedOpt.includes(normalizedSearchTerm) ||
        normalizedOpt.startsWith(normalizedSearchTerm);
    })
    : options;

  return (
    <div className="mb-0 relative" ref={wrapperRef}>
      <Label required={required}>{label}</Label>
      <div
        onClick={() => !isLoading && setIsOpen(!isOpen)}
        className={`h-[54px] w-full px-4 flex items-center justify-between bg-gray-50 dark:bg-gray-900 border rounded-2xl cursor-pointer transition-all duration-200 ${isOpen ? 'bg-white ring-2 ring-indigo-500 border-indigo-500' : 'border-gray-200 dark:border-gray-700 hover:bg-white dark:hover:bg-gray-950'} ${isLoading ? 'opacity-70 cursor-wait' : ''}`}
      >
        <span className={`font-medium truncate ${!selected ? 'text-gray-400' : 'text-gray-900 dark:text-white'}`}>
          {isLoading ? 'Yükleniyor...' : (selected || (placeholder || t('req_select')))}
        </span>
        {isLoading ? <Loader2 className="w-5 h-5 text-indigo-500 animate-spin" /> : <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180 text-indigo-500' : ''}`} />}
      </div>

      {isOpen && !isLoading && (
        <div className="absolute z-50 mt-2 w-full bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 max-h-80 overflow-y-auto custom-scrollbar animate-in fade-in slide-in-from-top-2 duration-200">
          {searchEnabled && (
            <div className="sticky top-0 bg-white dark:bg-gray-900 p-2 border-b border-gray-100 dark:border-gray-700 z-10">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                <input
                  autoFocus
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onInput={(e) => setSearchTerm((e.target as HTMLInputElement).value)}
                  placeholder="Ara..."
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  inputMode="search"
                  className="w-full pl-9 pr-4 py-2 bg-gray-50 dark:bg-gray-800 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:text-white"
                />
              </div>
            </div>
          )}
          {filteredOptions.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500">Sonuç bulunamadı</div>
          ) : (
            filteredOptions.map((opt: string) => (
              <div
                key={opt}
                onClick={() => { onChange(opt); setIsOpen(false); setSearchTerm(''); }}
                className={`px-5 py-3 text-sm font-medium cursor-pointer transition-colors flex items-center justify-between ${selected === opt ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
              >
                <span>{opt}</span>
                {selected === opt && <CheckCircle className="w-4 h-4 text-indigo-600" />}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const ModernMultiSelect = ({ options, selected, onChange, label, disabled = false, placeholder, isLoading = false, searchEnabled = false, required = false }: any) => {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [wrapperRef]);

  const toggleOption = (opt: string) => {
    if (selected.includes(opt)) onChange(selected.filter((s: string) => s !== opt));
    else onChange([...selected, opt]);
  };

  const getLabel = (val: string) => {
    if (val === 'Tümü') return 'TÜMÜ';
    // t() fonksiyonunun, anahtar bulunamazsa anahtarı geri döndürdüğünü varsayıyoruz.
    // Ancak bazen cache veya context yüklenmemiş olabilir, garanti olsun.
    return t(val as any) || val;
  };

  // Arama terimini normalize et
  const normalizedSearchTerm = normalizeTurkish(searchTerm);

  const filteredOptions = searchEnabled
    ? options.filter((opt: string) => {
      const normalizedOpt = normalizeTurkish(getLabel(opt));
      // Hem includes hem de startsWith kontrolü
      return normalizedOpt.includes(normalizedSearchTerm) ||
        normalizedOpt.startsWith(normalizedSearchTerm);
    })
    : options;

  return (
    <div className="mb-5 relative" ref={wrapperRef}>
      <Label required={required}>{label}</Label>
      <div
        onClick={() => !disabled && !isLoading && setIsOpen(!isOpen)}
        className={`min-h-[54px] w-full px-4 py-2 bg-gray-50 dark:bg-gray-900 border rounded-2xl flex flex-wrap items-center gap-2 transition-all duration-200 ${disabled || isLoading ? 'opacity-60 cursor-not-allowed bg-gray-100 dark:bg-gray-800' : 'cursor-pointer hover:bg-white dark:hover:bg-gray-950 hover:border-gray-300'
          } ${isOpen ? 'bg-white ring-2 ring-indigo-500 border-indigo-500' : 'border-gray-200 dark:border-gray-700'}`}
      >
        {selected.length === 0 && <span className="text-gray-400 text-sm font-medium">{isLoading ? 'Yükleniyor...' : (placeholder || t('req_select'))}</span>}
        {selected.map((val: string) => (
          <span key={val} className="inline-flex items-center px-3 py-1 rounded-lg text-sm font-semibold bg-white shadow-sm border border-gray-200 text-gray-700 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200">
            {getLabel(val)}
            {!disabled && <X className="w-3.5 h-3.5 ml-1.5 cursor-pointer text-gray-400 hover:text-red-500 transition-colors" onClick={(e) => { e.stopPropagation(); toggleOption(val); }} />}
          </span>
        ))}
        <div className="ml-auto pl-2">
          {isLoading ? <Loader2 className="w-5 h-5 text-indigo-500 animate-spin" /> : <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180 text-indigo-500' : ''}`} />}
        </div>
      </div>
      {isOpen && !disabled && !isLoading && (
        <div className="absolute z-50 mt-2 w-full bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 max-h-60 overflow-y-auto custom-scrollbar animate-in fade-in slide-in-from-top-2 duration-200">
          {searchEnabled && (
            <div className="sticky top-0 bg-white dark:bg-gray-900 p-2 border-b border-gray-100 dark:border-gray-700 z-10">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                <input
                  autoFocus
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchTerm(value);
                  }}
                  onInput={(e) => {
                    const value = (e.target as HTMLInputElement).value;
                    setSearchTerm(value);
                  }}
                  onKeyUp={(e) => {
                    const value = (e.target as HTMLInputElement).value;
                    setSearchTerm(value);
                  }}
                  placeholder="Ara..."
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  inputMode="search"
                  lang="tr"
                  className="w-full pl-9 pr-4 py-2 bg-gray-50 dark:bg-gray-800 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:text-white"
                />
              </div>
            </div>
          )}
          {filteredOptions.length === 0 ? <div className="px-4 py-3 text-sm text-gray-500">Sonuç bulunamadı</div> : filteredOptions.map((opt: string) => (
            <div key={opt} onClick={() => toggleOption(opt)} className={`px-5 py-3 text-sm font-medium cursor-pointer transition-colors ${selected.includes(opt) ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
              {getLabel(opt)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const RequestForm: React.FC = () => {
  const { categoryStr } = useParams();
  const category = categoryStr === 'emlak' ? Category.REAL_ESTATE : Category.CAR;
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { addToast } = useToast();
  const [step, setStep] = useState(1);
  const [subCategory, setSubCategory] = useState('EV');
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false); // New state for analysis

  const createLeadMutation = useCreateLead();

  const [cityList] = useState([...locationService.getCities()]);
  const [districtList, setDistrictList] = useState<string[]>([]);

  const [selectedCity, setSelectedCity] = useState('');
  const [selectedDistricts, setSelectedDistricts] = useState<string[]>([]);
  const [neighborhood, setNeighborhood] = useState('');

  const [urgentSelection, setUrgentSelection] = useState<'NONE' | 'URGENT' | 'SHOWCASE'>('NONE');
  const [isSeriousBuyer, setIsSeriousBuyer] = useState(false);

  const [consentKvkk, setConsentKvkk] = useState(false);
  const [consentShare, setConsentShare] = useState(false);

  const [showKvkkModal, setShowKvkkModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  // MARKET ANALYSIS STATE
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<any>(null);

  // PAYMENT STATE
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState<{ planName: string; price: number; onConfirm: (details: any) => Promise<void> } | null>(null);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [createdLeadId, setCreatedLeadId] = useState<string | null>(null);

  const [formData, setFormData] = useState<any>({
    listingType: category === Category.CAR ? ListingType.SALE : ListingType.RENT,
    title: '', description: '', budgetMin: '', budgetMax: '', currency: 'TL',
    roomCount: [], floor: [], heating: [], buildingAge: [], bathroom: [], kitchen: [], balcony: [], elevator: [], parking: [], furniture: [], site: [], credit: [], deed: [], usage: [],
    brand: [], model: [], yearMin: '', yearMax: '', fuel: [], gear: [], kmMin: '', kmMax: '', color: [], bodyType: [], damageStatus: [],
    maxOffers: 3,
    customerName: '', customerPhone: '', customerEmail: ''
  });

  const PROPERTY_SUB_TYPES = [
    { id: 'EV', label: t('req_cat_housing'), icon: <Home className="w-6 h-6" /> },
    { id: 'ISYERI', label: t('req_cat_workplace'), icon: <Building2 className="w-6 h-6" /> },
    { id: 'ARSA', label: t('req_cat_land'), icon: <Trees className="w-6 h-6" /> },
  ];

  const DOPING_OPTIONS = [
    { id: 'NONE', label: t('doping_std_label'), price: t('doping_std_price'), icon: CheckCircle, color: 'gray', desc: t('doping_std_desc') },
    { id: 'URGENT', label: t('doping_urgent_label'), price: t('doping_urgent_price'), icon: Zap, color: 'red', desc: t('doping_urgent_desc') },
  ];

  useEffect(() => {
    if (selectedCity) {
      const dists = locationService.getDistricts(selectedCity);
      setDistrictList(['Tümü', ...dists]);
    } else {
      setDistrictList([]);
    }
    setSelectedDistricts([]);
    setNeighborhood('');
  }, [selectedCity]);

  useEffect(() => {
    const currentUser = api.session.get();
    if (currentUser && currentUser.role === 'CUSTOMER') {
      setFormData((prev: any) => ({
        ...prev,
        customerName: currentUser.fullName || '',
        customerPhone: currentUser.phone || '',
        customerEmail: currentUser.email || ''
      }));
    }

    // Sayfa yüklendiğinde pending doping ödemesi var mı kontrol et
    const pendingPayment = localStorage.getItem('pendingDopingPayment');
    if (pendingPayment) {
      try {
        const paymentData = JSON.parse(pendingPayment);
        // 1 saat içinde yapılan ödemeler geçerli
        if (paymentData.completed && Date.now() - paymentData.timestamp < 3600000) {
          setPaymentCompleted(true);
        } else {
          // Süresi dolmuş, temizle
          localStorage.removeItem('pendingDopingPayment');
        }
      } catch (e) {
        localStorage.removeItem('pendingDopingPayment');
      }
    }
  }, []);

  const availableModels = formData.brand.length > 0 ? formData.brand.flatMap((brand: string) => Constants.CAR_MODELS[brand] || []) : [];

  const updateArray = (field: string, values: string[]) => setFormData((prev: any) => ({ ...prev, [field]: values }));

  const handleChange = (e: any) => {
    let { name, value } = e.target;
    if (name === 'customerPhone') {
      const raw = value.replace(/[^\d]/g, '');
      if (raw.length > 10) return;
      if (raw.length < 4) value = raw;
      else if (raw.length < 7) value = `(${raw.slice(0, 3)}) ${raw.slice(3)}`;
      else if (raw.length < 9) value = `(${raw.slice(0, 3)}) ${raw.slice(3, 6)}-${raw.slice(6)}`;
      else value = `(${raw.slice(0, 3)}) ${raw.slice(3, 6)}-${raw.slice(6, 8)}-${raw.slice(8, 10)}`;
    }
    if (['budgetMin', 'budgetMax', 'kmMin', 'kmMax'].includes(name)) {
      value = value.replace(/\D/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    }
    if (['yearMin', 'yearMax'].includes(name)) {
      value = value.replace(/\D/g, "").substring(0, 4);
    }
    setFormData((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleConsentClick = (type: 'KVKK' | 'SHARE') => {
    if (type === 'KVKK') {
      if (consentKvkk) setConsentKvkk(false);
      else setShowKvkkModal(true);
    } else {
      if (consentShare) setConsentShare(false);
      else setShowShareModal(true);
    }
  };

  const initiateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCity) { addToast('Lütfen Şehir Seçiniz', 'warning'); return; }
    if (!consentKvkk || !consentShare) {
      addToast(t('req_err_legal'), 'warning');
      return;
    }

    // ZORUNLU ALAN VALİDASYONU
    if (category === Category.REAL_ESTATE && subCategory === 'EV') {
      if (!formData.roomCount.length) { addToast('Lütfen oda sayısı seçiniz', 'warning'); return; }
      if (!formData.buildingAge.length) { addToast('Lütfen bina yaşı seçiniz', 'warning'); return; }
      if (!formData.floor.length) { addToast('Lütfen kat seçiniz', 'warning'); return; }
      if (!formData.heating.length) { addToast('Lütfen ısıtma tipi seçiniz', 'warning'); return; }
      if (!formData.bathroom.length) { addToast('Lütfen banyo seçiniz', 'warning'); return; }
      if (!formData.kitchen.length) { addToast('Lütfen mutfak tipi seçiniz', 'warning'); return; }
      if (!formData.balcony.length) { addToast('Lütfen balkon durumu seçiniz', 'warning'); return; }
      if (!formData.elevator.length) { addToast('Lütfen asansör durumu seçiniz', 'warning'); return; }
      if (!formData.parking.length) { addToast('Lütfen otopark durumu seçiniz', 'warning'); return; }
      if (!formData.furniture.length) { addToast('Lütfen eşya durumu seçiniz', 'warning'); return; }
      if (!formData.site.length) { addToast('Lütfen site durumu seçiniz', 'warning'); return; }
      // Satış için ek alanlar
      if (formData.listingType === ListingType.SALE) {
        if (!formData.credit.length) { addToast('Lütfen kredi uygunluğu seçiniz', 'warning'); return; }
        if (!formData.deed.length) { addToast('Lütfen tapu durumu seçiniz', 'warning'); return; }
        if (!formData.usage.length) { addToast('Lütfen kullanım türü seçiniz', 'warning'); return; }
      }
    }

    if (category === Category.CAR) {
      if (!formData.brand.length) { addToast('Lütfen marka seçiniz', 'warning'); return; }
      if (!formData.model.length) { addToast('Lütfen model seçiniz', 'warning'); return; }
      if (!formData.yearMin || !formData.yearMax) { addToast('Lütfen yıl aralığı giriniz', 'warning'); return; }
      if (!formData.kmMin || !formData.kmMax) { addToast('Lütfen kilometre aralığı giriniz', 'warning'); return; }
      if (!formData.fuel.length) { addToast('Lütfen yakıt tipi seçiniz', 'warning'); return; }
      if (!formData.gear.length) { addToast('Lütfen vites tipi seçiniz', 'warning'); return; }
      if (!formData.bodyType.length) { addToast('Lütfen kasa tipi seçiniz', 'warning'); return; }
      if (!formData.color.length) { addToast('Lütfen renk seçiniz', 'warning'); return; }
      if (!formData.damageStatus.length) { addToast('Lütfen hasar durumu seçiniz', 'warning'); return; }
    }

    // XSS PROTECTION: Validate inputs before processing
    const xssErrors = [
      validateInputXSS(formData.title, 'Başlık'),
      validateInputXSS(formData.description, 'Açıklama'),
      validateInputXSS(formData.customerName, 'Ad Soyad'),
    ].filter(Boolean);

    if (xssErrors.length > 0) {
      addToast(xssErrors[0] as string, 'error');
      return;
    }

    // --- AI MARKET ANALYSIS CHECK ---
    setIsAnalyzing(true);
    try {
      const analysis = await api.market.analyze({
        category: category,
        listingType: formData.listingType,
        budgetMax: formData.budgetMax,
        city: selectedCity
      });

      if (analysis.status === 'LOW') {
        // Bütçe düşükse uyarı modalını aç
        setAnalysisResult(analysis);
        setShowAnalysisModal(true);
        setIsAnalyzing(false);
        return; // Stop flow here
      }
    } catch (err) {
      console.error("Market analysis failed, continuing...", err);
    } finally {
      setIsAnalyzing(false);
    }

    // If check passed or failed silently, proceed
    // Eğer doping seçildiyse önce ödeme, değilse direkt SMS
    await initiatePayment();
  };

  const proceedToAuth = async () => {
    setShowAnalysisModal(false); // Close if open
    setIsLoading(true);
    try {
      await api.auth.sendOTP(formData.customerPhone);
      setStep(2);
    } catch (err: any) {
      addToast(err.message || 'SMS gönderilemedi', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaymentSuccess = async () => {
    setPaymentCompleted(true);
    setShowPaymentModal(false);
    // Ödeme başarılı olduğunu localStorage'a kaydet (sayfa yenilense bile bilgi kaybolmasın)
    localStorage.setItem('pendingDopingPayment', JSON.stringify({
      completed: true,
      timestamp: Date.now(),
      phone: formData.customerPhone
    }));
    // Ödeme başarılı, şimdi SMS gönder
    await proceedToAuth();
  };

  const initiatePayment = async () => {
    const isUrgent = urgentSelection !== 'NONE';
    if (!isUrgent) {
      // Doping seçilmediyse direkt SMS gönder
      await proceedToAuth();
      return;
    }

    // Doping seçildiyse önce ödeme yap
    const DOPING_PRICE = 249; // 249 TL
    const DOPING_PLAN_NAME = 'Hızlı Satış Dopingi (3 Gün)';

    const handleConfirm = async (paymentDetails: any) => {
      // Ödeme başlat (leadId olmadan, sadece doping için)
      // Kullanıcı henüz login olmamış olabilir, bu yüzden kullanıcı bilgilerini paymentDetails'e ekle
      return await api.transactions.buyCredits(
        'temp-user', // Geçici ID, backend'de kullanıcı bilgileri paymentDetails'ten alınacak
        DOPING_PRICE,
        0, // credits
        DOPING_PLAN_NAME,
        {
          ...paymentDetails,
          dopingDurationDays: 3,
          // Kullanıcı bilgileri (henüz login olmamış)
          buyerName: formData.customerName,
          buyerPhone: formData.customerPhone,
          buyerEmail: formData.customerEmail,
          buyerAddress: '',
          // leadId yok, çünkü henüz ilan oluşturulmadı
        }
      );
    };

    setPaymentDetails({
      planName: DOPING_PLAN_NAME,
      price: DOPING_PRICE,
      onConfirm: handleConfirm
    });
    setShowPaymentModal(true);
  };

  const handleVerifyAndSubmit = async () => {
    setIsVerifying(true);
    try {
      await api.auth.verifyOTP(formData.customerPhone, verificationCode);
      const user = await api.auth.loginCustomer(formData.customerPhone, formData.customerName, formData.customerEmail);
      api.session.save(user);

      const details: any = {};

      let locationString = "";
      if (selectedDistricts.length > 0 && !selectedDistricts.includes('Tümü')) {
        locationString = `${selectedCity} / ${selectedDistricts.join(', ')}`;
        if (neighborhood.trim()) {
          locationString += ` / ${neighborhood.trim()}`;
        }
      } else {
        locationString = `${selectedCity} / Tümü`;
      }

      details.doping = urgentSelection;
      details.isSeriousBuyer = isSeriousBuyer;

      if (category === Category.REAL_ESTATE) {
        details.subCategory = subCategory;
        if (subCategory === 'EV') {
          const mapping: any = { roomCount: 'req_label_room', floor: 'req_label_floor', heating: 'req_label_heating', buildingAge: 'req_label_age', bathroom: 'req_label_bath', kitchen: 'req_label_kitchen', balcony: 'req_label_balcony', elevator: 'req_label_elevator', parking: 'req_label_parking', furniture: 'req_label_furniture', site: 'req_label_site' };
          Object.keys(mapping).forEach(k => { if (formData[k].length) details[k] = formData[k]; });
          if (formData.listingType === ListingType.SALE) {
            ['credit', 'deed', 'usage'].forEach(k => { if (formData[k].length) details[k] = formData[k]; });
          }
        }
      } else {
        const mapping: any = { brand: 'req_label_brand', model: 'req_label_model', fuel: 'req_label_fuel', gear: 'req_label_gear', color: 'req_label_color', bodyType: 'req_label_body', damageStatus: 'req_label_damage' };
        Object.keys(mapping).forEach(k => { if (formData[k].length) details[k] = formData[k]; });
        if (formData.yearMin || formData.yearMax) details['yearRange'] = `${formData.yearMin} - ${formData.yearMax}`;
        if (formData.kmMin || formData.kmMax) details['kmRange'] = `${formData.kmMin} - ${formData.kmMax}`;
      }

      // Eğer ödeme yapıldıysa, details içine pendingDoping flag'i ekle
      // Backend'de ilan oluşturulurken isUrgent: false olacak ama pendingDoping: true olacak
      // Ödeme callback'inde bu flag kontrol edilip doping aktif olacak
      if (paymentCompleted && urgentSelection !== 'NONE') {
        details.pendingDoping = true;
      }

      // İlan oluşturulurken isUrgent her zaman false (ödeme yapılmadan önce)
      const isUrgent = false; // Ödeme yapılmadan önce her zaman false
      const urgentExpires = undefined; // Ödeme yapılmadan önce null

      const createdLead = await createLeadMutation.mutateAsync({
        category,
        listingType: formData.listingType,
        title: formData.title,
        description: formData.description || 'Detay belirtilmedi.',
        budget: `${formData.budgetMin} - ${formData.budgetMax} ${formData.currency}`,
        location: locationString,
        details,
        maxOffers: String(formData.maxOffers),
        isUrgent,
        isSeriousBuyer: isSeriousBuyer,
        urgentExpiresAt: urgentExpires,
        customerName: formData.customerName,
        customerPhone: formData.customerPhone,
        customerEmail: formData.customerEmail,
        customerId: user.id
      });

      // Eğer ödeme yapıldıysa, ödeme callback'inde leadId ile doping aktif olacak
      // Bu yüzden ödeme store'a leadId'yi eklememiz gerekiyor
      // Ama ödeme zaten yapıldı, callback'te leadId yok
      // Çözüm: Ödeme başarılı olduğunda localStorage'a leadId'yi kaydet
      // Ve callback'te bu leadId'yi kullan
      if (paymentCompleted && urgentSelection !== 'NONE') {
        // Ödeme zaten yapıldı, şimdi ilan oluşturuldu
        // Backend callback'te pendingDoping flag'i kontrol edilecek
        // Ama callback'te leadId yok, bu yüzden telefon numarasına göre bulmalıyız
        // Ya da ödeme store'a telefon numarasını ekleyip, callback'te telefon numarasına göre lead'i bulmalıyız
        // Doping ödemesi yapıldı, ilan oluşturuldu. Backend callback ile aktif olacak.
        if (process.env.NODE_ENV === 'development') {
          console.log('Doping ödemesi yapıldı, ilan oluşturuldu. Backend callback ile aktif olacak.');
        }
      }

      // Ödeme flag'ini temizle
      localStorage.removeItem('pendingDopingPayment');
      // Ödeme flag'ini temizle (ilan başarıyla oluşturuldu)
      localStorage.removeItem('pendingDopingPayment');
      setStep(3);
    } catch (error: any) {
      addToast(error.message || 'Doğrulama başarısız', 'error');
    } finally {
      setIsVerifying(false);
    }
  };

  // Kategoriye ve Alt Kategoriye Göre Özelleştirilmiş Placeholder'lar
  const getTitlePlaceholder = () => {
    if (category === Category.CAR) {
      return "Örn: 2020 Model Passat, Düşük KM, Hatasız";
    }

    if (subCategory === 'ISYERI') {
      return "Örn: Maslak'ta Plaza Katı, 200m2, Dekorasyonlu Ofis";
    }

    if (subCategory === 'ARSA') {
      return "Örn: Silivri'de Müstakil Tapulu, İmarlı Yatırımlık Arsa";
    }

    // Default: EV
    return "Örn: Kadıköy Moda'da 2+1, Deniz Manzaralı Kiralık Daire";
  };

  const getMainTitle = () => {
    return category === Category.REAL_ESTATE ? t('req_title_estate') : t('req_title_car');
  };

  const getDescriptionPlaceholder = () => {
    if (category === Category.CAR) {
      return t('req_desc_ph_car');
    }

    // Emlak Alt Kategorileri için
    if (category === Category.REAL_ESTATE) {
      if (subCategory === 'ARSA') {
        return "Örn: Müstakil tapulu, %30 imarlı, ifrazlı, yola cephesi olan, elektrik/su altyapısı hazır...";
      }
      if (subCategory === 'ISYERI') {
        if (formData.listingType === ListingType.RENT) {
          return "Örn: İşlek caddede, bacalı, ruhsata uygun, otopark sorunu olmayan, metroya yakın, devren veya boş...";
        }
        return "Örn: Kurumsal kiracılı, yüksek kira getirili, amortisman süresi kısa, yatırımlık dükkan/ofis...";
      }
      // Konut (EV)
      if (formData.listingType === ListingType.RENT) {
        return "Örn: Memur kefil verebilirim, aile apartmanı olsun, boyalı teslim, metrobuse yürüme mesafesinde...";
      }
      return t('req_desc_ph_estate');
    }

    return t('req_desc_ph');
  };

  if (step === 3) return (
    <div className="min-h-[80vh] flex items-center justify-center px-4">
      <div className="bg-white dark:bg-gray-800 p-8 rounded-[2rem] shadow-2xl max-w-lg w-full text-center">
        <div className="w-24 h-24 bg-green-50 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-12 h-12 text-green-500" />
        </div>
        <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white mb-4">{t('req_success_title')}</h2>
        <p className="text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">{t('req_success_desc')}</p>
        <button onClick={() => navigate('/')} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-4 rounded-xl font-bold transition-all transform hover:scale-[1.02] shadow-lg shadow-indigo-200 dark:shadow-none">{t('req_btn_home')}</button>
      </div>
    </div>
  );

  if (step === 2) return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="bg-white dark:bg-gray-800 rounded-[2rem] shadow-2xl p-8 max-w-md w-full border border-gray-100 dark:border-gray-700">
        <div className="w-20 h-20 bg-indigo-50 dark:bg-indigo-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <ShieldCheck className="w-10 h-10 text-indigo-600" />
        </div>
        <h3 className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-2">{t('req_security_title')}</h3>
        <p className="text-gray-500 text-center mb-8 text-sm">{t('req_security_desc')}</p>
        <input type="text" value={verificationCode} onChange={e => setVerificationCode(e.target.value)} placeholder="- - - -" maxLength={4} className="w-full text-center text-3xl font-mono tracking-[0.5em] h-16 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl mb-8 focus:ring-2 focus:ring-indigo-500 outline-none transition-all" />
        <div className="flex gap-4">
          <button onClick={() => setStep(1)} className="flex-1 py-4 border border-gray-200 dark:border-gray-700 rounded-xl font-bold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">{t('back')}</button>
          <button onClick={handleVerifyAndSubmit} disabled={isVerifying} className="flex-[2] bg-indigo-600 text-white py-4 rounded-xl font-bold shadow-lg hover:bg-indigo-700 transition-all">{isVerifying ? t('auth_processing') : t('req_btn_verify_publish')}</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6">
      <SEO
        title={category === Category.REAL_ESTATE ? "Emlak Talep Oluştur" : "Araç Talep Oluştur"}
        description={category === Category.REAL_ESTATE ? "Aradığınız evin kriterlerini girin, emlakçılar size teklif versin." : "Aradığınız aracın özelliklerini girin, galeriler size ulaşsın."}
      />
      <div className="max-w-4xl mx-auto">
        <button onClick={() => navigate('/')} className="flex items-center text-gray-500 hover:text-gray-800 dark:hover:text-white mb-6 font-bold transition-colors"><ArrowLeft className="w-5 h-5 mr-2" /> {t('back')}</button>
        <div className="bg-white dark:bg-gray-800 rounded-[2.5rem] shadow-xl border border-white/50 dark:border-gray-700 overflow-hidden relative">
          <div className="p-8 sm:p-12">
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 dark:text-white mb-2">{getMainTitle()}</h2>
            <p className="text-gray-500 dark:text-gray-400 mb-6">{t('req_subtitle')}</p>
            <RequiredFieldNote />
            <form onSubmit={initiateSubmit}>
              {category === Category.REAL_ESTATE && (
                <div className="space-y-8">
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, listingType: ListingType.RENT })}
                      className={formData.listingType === ListingType.RENT
                        ? "p-4 rounded-2xl font-bold text-lg border-2 transition-all duration-200 border-indigo-600 bg-indigo-50 text-indigo-700 shadow-sm"
                        : "p-4 rounded-2xl font-bold text-lg border-2 transition-all duration-200 border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-500 hover:bg-gray-100"
                      }
                    >
                      {t('req_type_rent')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, listingType: ListingType.SALE })}
                      className={formData.listingType === ListingType.SALE
                        ? "p-4 rounded-2xl font-bold text-lg border-2 transition-all duration-200 border-indigo-600 bg-indigo-50 text-indigo-700 shadow-sm"
                        : "p-4 rounded-2xl font-bold text-lg border-2 transition-all duration-200 border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-500 hover:bg-gray-100"
                      }
                    >
                      {t('req_type_sale')}
                    </button>
                  </div>

                  <div>
                    <Label>{t('prov_label_category')}</Label>
                    <div className="grid grid-cols-3 gap-3">
                      {PROPERTY_SUB_TYPES.map(t => (
                        <div
                          key={t.id}
                          onClick={() => setSubCategory(t.id)}
                          className={subCategory === t.id
                            ? "py-4 px-2 border rounded-2xl flex flex-col items-center cursor-pointer transition-all duration-200 border-indigo-600 bg-indigo-50 text-indigo-700 shadow-md transform scale-[1.02]"
                            : "py-4 px-2 border rounded-2xl flex flex-col items-center cursor-pointer transition-all duration-200 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
                          }
                        >
                          <div className={subCategory === t.id ? 'text-indigo-600' : 'text-gray-400'}>{t.icon}</div>
                          <span className="text-xs font-bold mt-2">{t.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* KONUM SEÇİMİ - Kategori altında */}
                  <div className="p-6 bg-blue-50/50 dark:bg-blue-900/10 rounded-3xl border border-blue-100 dark:border-blue-900/20">
                    <h4 className="font-bold text-sm text-blue-700 dark:text-blue-400 mb-5 uppercase tracking-wide flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                      Konum
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <ModernSingleSelect
                        required={true}
                        label={t('req_city')}
                        options={cityList}
                        selected={selectedCity}
                        onChange={(val: string) => { setSelectedCity(val); setSelectedDistricts([]); setNeighborhood(''); }}
                        searchEnabled={true}
                      />
                      {selectedCity && (
                        <ModernMultiSelect
                          required={true}
                          label={t('req_district')}
                          options={districtList}
                          selected={selectedDistricts}
                          onChange={(newArr: string[]) => {
                            const addedTumu = !selectedDistricts.includes('Tümü') && newArr.includes('Tümü');
                            const hasTumuAndOthers = newArr.includes('Tümü') && newArr.length > 1;
                            if (addedTumu) {
                              setSelectedDistricts(['Tümü']);
                            } else if (hasTumuAndOthers) {
                              setSelectedDistricts(newArr.filter(x => x !== 'Tümü'));
                            } else {
                              setSelectedDistricts(newArr);
                            }
                            setNeighborhood('');
                          }}
                          placeholder={t('req_select')}
                          searchEnabled={true}
                        />
                      )}
                    </div>
                    {selectedCity && selectedDistricts.length > 0 && !selectedDistricts.includes('Tümü') && (
                      <div className="mt-4">
                        <Label>Mahalle (Opsiyonel)</Label>
                        <input
                          value={neighborhood}
                          onChange={e => setNeighborhood(e.target.value)}
                          className={InputBaseClass}
                          placeholder="Örn: Bağdat Caddesi, Caferağa, Fenerbahçe..."
                        />
                      </div>
                    )}
                  </div>

                  {subCategory === 'EV' && (
                    <div className="animate-in fade-in slide-in-from-top-4 space-y-5 border-t border-dashed border-gray-200 dark:border-gray-700 pt-8 mt-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <ModernMultiSelect required={true} label={t('req_label_room')} options={Constants.RE_ROOM_OPTIONS} selected={formData.roomCount} onChange={(v: any) => updateArray('roomCount', v)} />
                        <ModernMultiSelect required={true} label={t('req_label_age')} options={Constants.RE_BUILDING_AGE} selected={formData.buildingAge} onChange={(v: any) => updateArray('buildingAge', v)} />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <ModernMultiSelect required={true} label={t('req_label_floor')} options={Constants.RE_FLOOR_OPTIONS} selected={formData.floor} onChange={(v: any) => updateArray('floor', v)} />
                        <ModernMultiSelect required={true} label={t('req_label_heating')} options={Constants.RE_HEATING_OPTIONS} selected={formData.heating} onChange={(v: any) => updateArray('heating', v)} />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <ModernMultiSelect required={true} label={t('req_label_bath')} options={Constants.RE_BATHROOM} selected={formData.bathroom} onChange={(v: any) => updateArray('bathroom', v)} />
                        <ModernMultiSelect required={true} label={t('req_label_kitchen')} options={Constants.RE_KITCHEN} selected={formData.kitchen} onChange={(v: any) => updateArray('kitchen', v)} />
                      </div>
                      <div className="p-6 bg-gray-50/80 dark:bg-gray-900/40 rounded-3xl border border-gray-100 dark:border-gray-700/50">
                        <h4 className="font-bold text-sm mb-6 dark:text-gray-300 flex items-center uppercase tracking-wide text-gray-500"><Sparkles className="w-4 h-4 mr-2" /> {t('req_label_extra')}</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                          <ModernMultiSelect required={true} label={t('req_label_balcony')} options={Constants.RE_BALCONY} selected={formData.balcony} onChange={(v: any) => updateArray('balcony', v)} />
                          <ModernMultiSelect required={true} label={t('req_label_elevator')} options={Constants.RE_ELEVATOR} selected={formData.elevator} onChange={(v: any) => updateArray('elevator', v)} />
                          <ModernMultiSelect required={true} label={t('req_label_parking')} options={Constants.RE_PARKING} selected={formData.parking} onChange={(v: any) => updateArray('parking', v)} />
                          <ModernMultiSelect required={true} label={t('req_label_furniture')} options={Constants.RE_FURNITURE} selected={formData.furniture} onChange={(v: any) => updateArray('furniture', v)} />
                          <ModernMultiSelect required={true} label={t('req_label_site')} options={Constants.RE_SITE} selected={formData.site} onChange={(v: any) => updateArray('site', v)} />
                        </div>
                      </div>
                      {formData.listingType === ListingType.SALE && (
                        <div className="p-6 bg-amber-50/80 dark:bg-amber-900/10 rounded-3xl border border-amber-100 dark:border-amber-900/20">
                          <h4 className="font-bold text-sm text-amber-700 dark:text-amber-500 mb-6 uppercase tracking-wide flex items-center"><Banknote className="w-4 h-4 mr-2" /> {t('req_label_sale_details')}</h4>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                            <ModernMultiSelect required={true} label={t('req_label_credit')} options={Constants.RE_CREDIT} selected={formData.credit} onChange={(v: any) => updateArray('credit', v)} />
                            <ModernMultiSelect required={true} label={t('req_label_deed')} options={Constants.RE_DEED} selected={formData.deed} onChange={(v: any) => updateArray('deed', v)} />
                            <ModernMultiSelect required={true} label={t('req_label_usage')} options={Constants.RE_USAGE} selected={formData.usage} onChange={(v: any) => updateArray('usage', v)} />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {category === Category.CAR && (
                <div className="space-y-6">
                  {/* KONUM SEÇİMİ - Araç Formu */}
                  <div className="p-6 bg-blue-50/50 dark:bg-blue-900/10 rounded-3xl border border-blue-100 dark:border-blue-900/20">
                    <h4 className="font-bold text-sm text-blue-700 dark:text-blue-400 mb-5 uppercase tracking-wide flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                      Konum
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <ModernSingleSelect
                        required={true}
                        label={t('req_city')}
                        options={cityList}
                        selected={selectedCity}
                        onChange={(val: string) => { setSelectedCity(val); setSelectedDistricts([]); setNeighborhood(''); }}
                        searchEnabled={true}
                      />
                      {selectedCity && (
                        <ModernMultiSelect
                          required={true}
                          label={t('req_district')}
                          options={districtList}
                          selected={selectedDistricts}
                          onChange={(newArr: string[]) => {
                            const addedTumu = !selectedDistricts.includes('Tümü') && newArr.includes('Tümü');
                            const hasTumuAndOthers = newArr.includes('Tümü') && newArr.length > 1;
                            if (addedTumu) {
                              setSelectedDistricts(['Tümü']);
                            } else if (hasTumuAndOthers) {
                              setSelectedDistricts(newArr.filter(x => x !== 'Tümü'));
                            } else {
                              setSelectedDistricts(newArr);
                            }
                            setNeighborhood('');
                          }}
                          placeholder={t('req_select')}
                          searchEnabled={true}
                        />
                      )}
                    </div>
                    {selectedCity && selectedDistricts.length > 0 && !selectedDistricts.includes('Tümü') && (
                      <div className="mt-4">
                        <Label>Mahalle (Opsiyonel)</Label>
                        <input
                          value={neighborhood}
                          onChange={e => setNeighborhood(e.target.value)}
                          className={InputBaseClass}
                          placeholder="Örn: Bağdat Caddesi, Caferağa, Fenerbahçe..."
                        />
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <ModernMultiSelect required={true} label={t('req_label_brand')} options={Constants.CAR_BRANDS} selected={formData.brand} onChange={(v: any) => updateArray('brand', v)} />
                    <ModernMultiSelect required={true} label={t('req_label_model')} options={availableModels} selected={formData.model} onChange={(v: any) => updateArray('model', v)} disabled={formData.brand.length === 0} placeholder={formData.brand.length === 0 ? t('req_ph_brand') : t('req_select')} />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <Label required={true}>{t('req_label_year_range')}</Label>
                      <div className="flex gap-3">
                        <input name="yearMin" placeholder={t('req_min')} value={formData.yearMin} onChange={handleChange} className={InputBaseClass} maxLength={4} />
                        <input name="yearMax" placeholder={t('req_max')} value={formData.yearMax} onChange={handleChange} className={InputBaseClass} maxLength={4} />
                      </div>
                    </div>
                    <div>
                      <Label required={true}>{t('req_label_km_range')}</Label>
                      <div className="flex gap-3">
                        <input name="kmMin" placeholder={t('req_min')} value={formData.kmMin} onChange={handleChange} className={InputBaseClass} />
                        <input name="kmMax" placeholder={t('req_max')} value={formData.kmMax} onChange={handleChange} className={InputBaseClass} />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <ModernMultiSelect required={true} label={t('req_label_fuel')} options={Constants.CAR_FUEL_TYPES} selected={formData.fuel} onChange={(v: any) => updateArray('fuel', v)} />
                    <ModernMultiSelect required={true} label={t('req_label_gear')} options={Constants.CAR_GEAR_TYPES} selected={formData.gear} onChange={(v: any) => updateArray('gear', v)} />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <ModernMultiSelect required={true} label={t('req_label_body')} options={Constants.CAR_BODY_TYPES} selected={formData.bodyType} onChange={(v: any) => updateArray('bodyType', v)} />
                    <ModernMultiSelect required={true} label={t('req_label_color')} options={Constants.CAR_COLORS} selected={formData.color} onChange={(v: any) => updateArray('color', v)} />
                    <ModernMultiSelect required={true} label={t('req_label_damage')} options={Constants.CAR_DAMAGE} selected={formData.damageStatus} onChange={(v: any) => updateArray('damageStatus', v)} />
                  </div>
                </div>
              )}

              <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-700 space-y-8">
                <div>
                  <Label>{t('req_title_label')}</Label>
                  <input name="title" required value={formData.title} onChange={handleChange} className={InputBaseClass} placeholder={getTitlePlaceholder()} />
                </div>

                <div className="p-5 bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-900/10 dark:to-green-900/10 rounded-2xl border border-emerald-100 dark:border-emerald-800">
                  <Label>{t('req_buyer_status_label')}</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                    <div
                      onClick={() => setIsSeriousBuyer(false)}
                      className={!isSeriousBuyer
                        ? "p-4 rounded-xl border-2 cursor-pointer transition-all flex items-start border-gray-400 bg-white dark:bg-gray-800 shadow-sm"
                        : "p-4 rounded-xl border-2 cursor-pointer transition-all flex items-start border-gray-200 dark:border-gray-700 hover:border-gray-300 bg-transparent opacity-70"
                      }
                    >
                      <div className={!isSeriousBuyer
                        ? "w-5 h-5 rounded-full border-2 mr-3 mt-0.5 flex items-center justify-center border-gray-600"
                        : "w-5 h-5 rounded-full border-2 mr-3 mt-0.5 flex items-center justify-center border-gray-300"
                      }>
                        {!isSeriousBuyer && <div className="w-2.5 h-2.5 bg-gray-600 rounded-full"></div>}
                      </div>
                      <div>
                        <h4 className="font-bold text-sm text-gray-900 dark:text-white">{t('req_buyer_standard')}</h4>
                        <p className="text-xs text-gray-500 mt-1">{t('req_buyer_standard_desc')}</p>
                      </div>
                    </div>
                    <div
                      onClick={() => setIsSeriousBuyer(true)}
                      className={isSeriousBuyer
                        ? "p-4 rounded-xl border-2 cursor-pointer transition-all flex items-start border-emerald-500 bg-white dark:bg-gray-800 shadow-md ring-2 ring-emerald-100 dark:ring-emerald-900/30"
                        : "p-4 rounded-xl border-2 cursor-pointer transition-all flex items-start border-gray-200 dark:border-gray-700 hover:border-emerald-300 bg-transparent"
                      }
                    >
                      <div className={isSeriousBuyer
                        ? "w-5 h-5 rounded-full border-2 mr-3 mt-0.5 flex items-center justify-center border-emerald-500"
                        : "w-5 h-5 rounded-full border-2 mr-3 mt-0.5 flex items-center justify-center border-gray-300"
                      }>
                        {isSeriousBuyer && <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full"></div>}
                      </div>
                      <div>
                        <h4 className="font-bold text-sm text-emerald-700 dark:text-emerald-400">{t('req_buyer_serious')}</h4>
                        <p className="text-xs text-emerald-600/70 dark:text-emerald-500/70 mt-1">{t('req_buyer_serious_desc')}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-900/50 p-6 rounded-3xl border border-gray-100 dark:border-gray-700/50">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="font-bold text-lg text-gray-800 dark:text-white flex items-center"><Banknote className="w-5 h-5 mr-2 text-indigo-500" /> {t('req_budget_header')}</h3>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="col-span-2 md:col-span-1">
                      <Label>{t('req_currency')}</Label>
                      <SelectWrapper>
                        <select name="currency" value={formData.currency} onChange={handleChange} className={`${InputBaseClass} appearance-none cursor-pointer`}>
                          {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </SelectWrapper>
                    </div>
                    <div className="col-span-1">
                      <Label>{t('req_min')}</Label>
                      <input name="budgetMin" required value={formData.budgetMin} onChange={handleChange} className={InputBaseClass} placeholder="0" />
                    </div>
                    <div className="col-span-1">
                      <Label>{t('req_max')}</Label>
                      <input name="budgetMax" required value={formData.budgetMax} onChange={handleChange} className={InputBaseClass} placeholder="0" />
                    </div>
                  </div>
                </div>

                <div>
                  <Label>{t('req_desc_label')}</Label>
                  <textarea name="description" placeholder={getDescriptionPlaceholder()} value={formData.description} onChange={handleChange} className={TextareaBaseClass} />
                </div>
                <div>
                  <Label>{t('req_offers_label')}</Label>
                  <div className="grid grid-cols-4 gap-3">
                    {[{ v: 3, l: '3' }, { v: 5, l: '5' }, { v: 10, l: '10' }, { v: 'UNLIMITED', l: t('req_offer_unlimited') }].map(opt => (
                      <div
                        key={opt.v}
                        onClick={() => setFormData({ ...formData, maxOffers: opt.v })}
                        className={formData.maxOffers === opt.v
                          ? "cursor-pointer py-3 border rounded-xl text-center transition-all duration-200 bg-indigo-600 text-white border-indigo-600 shadow-md font-bold transform scale-105"
                          : "cursor-pointer py-3 border rounded-xl text-center transition-all duration-200 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-indigo-300"
                        }
                      >
                        <span className="text-sm">{opt.l}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>{t('req_urgent_title')}</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {DOPING_OPTIONS.map((opt) => {
                      const isSelected = urgentSelection === opt.id;
                      const Icon = opt.icon;
                      let activeClass = '';
                      if (isSelected) {
                        if (opt.color === 'red') activeClass = 'bg-red-50 border-red-500 dark:bg-red-900/20';
                        else if (opt.color === 'purple') activeClass = 'bg-purple-50 border-purple-500 dark:bg-purple-900/20';
                        else activeClass = 'bg-gray-50 border-gray-500 dark:bg-gray-800';
                      } else {
                        activeClass = 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 hover:border-gray-300';
                      }

                      return (
                        <div
                          key={opt.id}
                          onClick={() => setUrgentSelection(opt.id as any)}
                          className={`relative cursor-pointer border-2 rounded-2xl p-4 transition-all duration-300 flex flex-col items-center text-center ${activeClass} ${isSelected ? 'transform scale-[1.02] shadow-md' : 'opacity-80 hover:opacity-100'}`}
                        >
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${isSelected ? (opt.color === 'red' ? 'bg-red-500 text-white' : (opt.color === 'purple' ? 'bg-purple-600 text-white' : 'bg-gray-500 text-white')) : 'bg-gray-100 dark:bg-gray-800 text-gray-400'}`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <h4 className={`font-bold text-sm ${isSelected ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'}`}>{opt.label}</h4>
                          <span className={`text-xs font-bold mt-1 ${opt.price === 'Ücretsiz' || opt.price === 'Free' ? 'text-green-600' : 'text-indigo-600'}`}>{opt.price}</span>
                          <p className="text-[10px] text-gray-400 mt-2 leading-tight">{opt.desc}</p>
                          {isSelected && <div className={`absolute top-2 right-2 w-3 h-3 rounded-full ${opt.color === 'red' ? 'bg-red-500' : (opt.color === 'purple' ? 'bg-purple-500' : 'bg-gray-500')}`}></div>}
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-400 mt-3 text-center">
                    {t('doping_note_prefix')} <span className="font-bold text-gray-500 dark:text-gray-300">3 {t('doping_days_left').split(' ')[0]}</span> {t('doping_note_suffix')}
                  </p>
                </div>

                <div className="bg-indigo-50/50 dark:bg-gray-900/50 p-8 rounded-[2rem] border border-indigo-50 dark:border-gray-700">
                  <h4 className="font-bold text-gray-900 dark:text-white mb-6 flex items-center uppercase tracking-wide text-sm"><ShieldCheck className="w-5 h-5 mr-2 text-indigo-600" /> {t('req_contact_header')}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <input name="customerName" placeholder={t('auth_ph_contact')} required value={formData.customerName} onChange={handleChange} className={InputBaseClass} />
                    <input name="customerPhone" placeholder={t('auth_ph_phone')} required value={formData.customerPhone} onChange={handleChange} className={InputBaseClass} />
                    <input name="customerEmail" placeholder={t('auth_ph_email')} required value={formData.customerEmail} onChange={handleChange} className={`${InputBaseClass} md:col-span-2`} />
                  </div>
                  <p className="text-xs text-gray-500 mt-4 text-center">{t('req_contact_note')}</p>
                </div>

                <div className="bg-gray-50 dark:bg-gray-900/30 p-4 rounded-xl space-y-3 border border-gray-100 dark:border-gray-800">
                  <div className="flex items-start cursor-pointer" onClick={() => handleConsentClick('KVKK')}>
                    <div
                      className={`w-5 h-5 min-w-[1.25rem] border-2 rounded mt-0.5 mr-3 flex items-center justify-center transition-all ${consentKvkk ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'}`}
                    >
                      {consentKvkk && <CheckCircle className="w-4 h-4 text-white" />}
                    </div>
                    <span className="text-sm text-indigo-600 dark:text-indigo-400 font-medium select-none hover:underline flex-1">
                      {t('req_legal_kvkk')}
                    </span>
                  </div>
                  <div className="flex items-start cursor-pointer" onClick={() => handleConsentClick('SHARE')}>
                    <div
                      className={`w-5 h-5 min-w-[1.25rem] border-2 rounded mt-0.5 mr-3 flex items-center justify-center transition-all ${consentShare ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'}`}
                    >
                      {consentShare && <CheckCircle className="w-4 h-4 text-white" />}
                    </div>
                    <span className="text-sm text-indigo-600 dark:text-indigo-400 font-medium select-none hover:underline flex-1">
                      {t('req_legal_share')}
                    </span>
                  </div>
                </div>
              </div>
              <div className="mt-8">
                <button type="submit" disabled={isAnalyzing || isLoading} className="w-full py-5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-2xl font-bold text-lg shadow-xl shadow-indigo-200 dark:shadow-none transition-all transform hover:scale-[1.01] flex items-center justify-center">
                  {isAnalyzing ? <Loader2 className="w-6 h-6 animate-spin mr-2" /> : t('req_btn_next')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* MARKET ANALYSIS MODAL */}
      {showAnalysisModal && analysisResult && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/90 backdrop-blur-sm animate-in fade-in zoom-in-95">
          <div className="bg-white dark:bg-gray-800 rounded-[2rem] max-w-lg w-full p-8 relative shadow-2xl border border-gray-100 dark:border-gray-700">
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-white dark:border-gray-700 shadow-sm">
                <TrendingUp className="w-10 h-10 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="text-2xl font-black text-gray-900 dark:text-white mb-2">Gerçeklik Kontrolü</h3>
              <p className="text-gray-600 dark:text-gray-300">Bütçen piyasa ortalamasının altında görünüyor.</p>
            </div>

            <div className="bg-gray-50 dark:bg-gray-900/50 p-6 rounded-2xl border border-gray-100 dark:border-gray-700 mb-6">
              <div className="flex justify-between items-center mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                <span className="text-sm text-gray-500">Piyasa Ortalaması</span>
                <span className="font-bold text-gray-900 dark:text-white">{analysisResult.avgPrice.toLocaleString()} {formData.currency}</span>
              </div>
              <div className="flex justify-between items-center text-red-500 font-bold">
                <span className="text-sm">Senin Bütçen</span>
                <span>{formData.budgetMax.toLocaleString()} {formData.currency}</span>
              </div>
              <p className="text-xs text-red-400 mt-2 text-right">Ortalamanın %{Math.abs(analysisResult.diffPercent)} altında</p>
            </div>

            <div className="space-y-3 mb-8">
              <h4 className="font-bold text-gray-900 dark:text-white text-sm flex items-center"><Sparkles className="w-4 h-4 mr-2 text-indigo-500" /> Şansını Artırmak İçin:</h4>
              <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-2 list-disc pl-5">
                <li>Bütçeni piyasa ortalamasına yaklaştırabilirsin.</li>
                <li>Daha geniş bir bölge seçebilirsin (Tüm İlçe).</li>
                <li>Bakım ve Dekorasyon İhtiyacı Olan seçeneklerini değerlendirebilirsin.</li>
              </ul>
            </div>

            <div className="flex flex-col gap-3">
              <button onClick={() => setShowAnalysisModal(false)} className="w-full py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold shadow-lg transition-all">Bütçemi Düzenle</button>
              <button onClick={proceedToAuth} className="w-full py-3.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-xl font-bold transition-all">Yine de Devam Et</button>
            </div>
          </div>
        </div>
      )}

      {showKvkkModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-gray-900/80 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-2xl w-full p-8 relative shadow-2xl flex flex-col max-h-[85vh]">
            <button onClick={() => setShowKvkkModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-white"><X className="w-6 h-6" /></button>
            <h3 className="text-xl font-bold mb-4 dark:text-white flex items-center shrink-0"><FileText className="w-5 h-5 mr-2" /> {t('legal_kvkk_title')}</h3>
            <div className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed overflow-y-auto custom-scrollbar whitespace-pre-wrap flex-1 border-y border-gray-100 dark:border-gray-700 py-4 my-2">
              {t('legal_kvkk_text')}
            </div>
            <button onClick={() => { setConsentKvkk(true); setShowKvkkModal(false); }} className="w-full mt-4 bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-xl font-bold transition-colors shrink-0">Okudum, anladım ve onaylıyorum</button>
          </div>
        </div>
      )}

      {showShareModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-gray-900/80 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-2xl w-full p-8 relative shadow-2xl flex flex-col max-h-[85vh]">
            <button onClick={() => setShowShareModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-white"><X className="w-6 h-6" /></button>
            <h3 className="text-xl font-bold mb-4 dark:text-white flex items-center shrink-0"><ShieldCheck className="w-5 h-5 mr-2" /> {t('legal_share_title')}</h3>
            <div className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed overflow-y-auto custom-scrollbar whitespace-pre-wrap flex-1 border-y border-gray-100 dark:border-gray-700 py-4 my-2">
              {t('legal_share_text')}
            </div>
            <button onClick={() => { setConsentShare(true); setShowShareModal(false); }} className="w-full mt-4 bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-xl font-bold transition-colors shrink-0">Okudum, anladım ve onaylıyorum</button>
          </div>
        </div>
      )}

      {/* Payment Modal - Doping Ödemesi İçin */}
      {showPaymentModal && paymentDetails && (
        <PaymentModal
          theme="light"
          planName={paymentDetails.planName}
          price={paymentDetails.price}
          credits={0}
          onClose={() => {
            setShowPaymentModal(false);
            setPaymentDetails(null);
          }}
          onConfirm={paymentDetails.onConfirm}
          onSuccess={handlePaymentSuccess}
        />
      )}
    </div>
  );
};

export default RequestForm;
